import React from "react";
import { Link } from "react-router-dom";
import { Award, CheckCircle2, ArrowUpRight, Users, Briefcase, BadgeCheck, Sparkles, GraduationCap } from "lucide-react";

const PILLARS = [
  {
    tag: "01",
    title: "Technical Excellence",
    lead: "Proficiency in your chosen domain through instructor-led learning, hands-on projects and rigorous technical assessments.",
    items: [
      "Cloud & Infrastructure",
      "Cybersecurity",
      "Networking",
      "Software Development",
      "AI & Data Analytics",
      "IT Service Management",
      "Digital Transformation",
    ],
  },
  {
    tag: "02",
    title: "Professional Excellence",
    lead: "The everyday skills organizations value most — the ones that turn a candidate into a colleague.",
    items: [
      "Business communication",
      "Professional writing",
      "Presentation skills",
      "Stakeholder management",
      "Critical thinking",
      "Time management",
      "Workplace ethics",
      "Collaboration",
    ],
  },
  {
    tag: "03",
    title: "Business Readiness",
    lead: "How technology supports business objectives — the commercial context that separates a coder from a consultant.",
    items: [
      "Business processes",
      "Customer-centric thinking",
      "Project lifecycle",
      "Agile fundamentals",
      "Service delivery",
      "Consulting mindset",
      "Problem-solving",
    ],
  },
  {
    tag: "04",
    title: "Leadership & Growth",
    lead: "The mindset every RCP develops for long-term career success — how you show up when nobody is watching.",
    items: [
      "Ownership",
      "Accountability",
      "Innovation",
      "Emotional intelligence",
      "Adaptability",
      "Continuous learning",
    ],
  },
];

const LEVELS = [
  {
    badge: "🥉",
    tone: "border-amber-700 bg-amber-50",
    title: "RCP Associate",
    for: "Fresh graduates & early-career professionals",
    demonstrates: [
      "Foundational technical competency",
      "Professional behavior",
      "Workplace readiness",
    ],
  },
  {
    badge: "🥈",
    tone: "border-slate-400 bg-slate-50",
    title: "RCP Professional",
    for: "Professionals with 2–6 years of industry experience",
    demonstrates: [
      "Advanced technical capability",
      "Client-facing skills",
      "Solution-oriented thinking",
    ],
  },
  {
    badge: "🥇",
    tone: "border-yellow-500 bg-yellow-50",
    title: "RCP Expert",
    for: "Senior consultants & domain specialists (7+ years)",
    demonstrates: [
      "Leadership across delivery teams",
      "Architecture & design expertise",
      "Strategic consulting",
      "Business transformation capability",
    ],
  },
  {
    badge: "💎",
    tone: "border-navy bg-navy text-white",
    title: "RCP Elite",
    for: "Exceptional professionals who mentor others & deliver measurable business value",
    demonstrates: [
      "Sustained impact across engagements",
      "Recognized thought leadership",
      "Mentorship of RCP cohorts",
    ],
  },
];

const REQUIREMENTS = [
  "Attendance and participation",
  "Technical assessments",
  "Practical assignments",
  "Live projects or simulations",
  "Professional communication evaluation",
  "Interview readiness",
  "Behavioral assessment",
  "Final certification review",
];

const PRO_BENEFITS = [
  ["Recognized certification", "A formal Ravemora Certified Professional credential — respected by our hiring network."],
  ["Digital certificate & credential", "A verifiable digital badge you can add to LinkedIn, résumé and email signatures."],
  ["Continuous learning access", "Ongoing upskilling paths, refresh modules and specialization tracks."],
  ["Career guidance", "One-on-one mentorship, résumé reviews and role-specific coaching."],
  ["Placement support", "Prioritized shortlisting with Ravemora client engagements."],
  ["Networking & community", "Membership in the Ravemora Professional Community — peers, alumni and hiring managers."],
];

const EMP_BENEFITS = [
  "Technically validated",
  "Professionally trained",
  "Business aware",
  "Communication ready",
  "Interview prepared",
  "Deployment ready",
  "Continuously learning",
];

export default function RCPProgram() {
  return (
    <div className="space-y-16" data-testid="rcp-program-page">
      {/* Hero */}
      <section className="border-b border-navy/10 pb-10">
        <div className="flex items-center gap-2 label-eyebrow text-gold">
          <Award size={14} /> Ravemora Certified Professional
        </div>
        <h1 className="serif-display text-4xl md:text-5xl lg:text-6xl mt-6 leading-[1.05] text-navy">
          More than a certification. <br />
          <span className="text-gold">A commitment to excellence.</span>
        </h1>
        <p className="mt-6 max-w-3xl text-lg text-navy/70 leading-relaxed">
          The <strong>RCP Program</strong> is Ravemora's structured pathway for professionals who want to be
          hired, trusted and remembered. It validates not only what you know, but how you show up
          — combining <em>technical mastery</em>, <em>professional maturity</em>, <em>business context</em> and
          a <em>leadership mindset</em>.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/learning" className="btn-outline inline-flex items-center gap-2" data-testid="rcp-public-page">
            Explore Learning <ArrowUpRight size={14}/>
          </Link>
          <a href="/Ravemora-RCP-Candidate-Program.pptx" className="btn-primary inline-flex items-center gap-2" data-testid="rcp-download-ppt">
            Download the program deck <ArrowUpRight size={14}/>
          </a>
        </div>
      </section>

      {/* The framework */}
      <section data-testid="rcp-framework">
        <div className="label-eyebrow text-gold">The Framework</div>
        <h2 className="serif-display text-3xl md:text-4xl text-navy mt-2">Four pillars. One standard.</h2>
        <p className="mt-4 max-w-3xl text-navy/70">Every RCP candidate is trained and evaluated across four dimensions before certification is issued.</p>
        <div className="mt-10 grid md:grid-cols-2 gap-8">
          {PILLARS.map((p) => (
            <div key={p.tag} className="border border-navy/10 bg-white p-8 hover:border-gold transition-colors" data-testid={`rcp-pillar-${p.tag}`}>
              <div className="flex items-baseline gap-4">
                <div className="serif-display text-gold text-5xl leading-none">{p.tag}</div>
                <h3 className="serif-display text-2xl text-navy leading-tight">{p.title}</h3>
              </div>
              <p className="mt-4 text-navy/70 leading-relaxed">{p.lead}</p>
              <ul className="mt-6 grid grid-cols-2 gap-y-2">
                {p.items.map((it) => (
                  <li key={it} className="flex items-baseline gap-2 text-sm text-navy/80">
                    <CheckCircle2 size={14} className="text-gold shrink-0 relative top-0.5"/> {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Certification levels */}
      <section data-testid="rcp-levels">
        <div className="label-eyebrow text-gold">Certification Levels</div>
        <h2 className="serif-display text-3xl md:text-4xl text-navy mt-2">Grow with the credential.</h2>
        <p className="mt-4 max-w-3xl text-navy/70">Rather than issuing one generic certificate, Ravemora offers four progressive levels — so your credential grows with your career.</p>
        <div className="mt-10 grid md:grid-cols-2 xl:grid-cols-4 gap-6">
          {LEVELS.map((lv, i) => (
            <div key={lv.title} className={`border-2 p-6 ${lv.tone}`} data-testid={`rcp-level-${i+1}`}>
              <div className="text-5xl leading-none">{lv.badge}</div>
              <h3 className={`serif-display text-2xl mt-4 leading-tight ${lv.tone.includes("navy") ? "text-white" : "text-navy"}`}>{lv.title}</h3>
              <div className={`text-xs uppercase tracking-wider mt-3 ${lv.tone.includes("navy") ? "text-gold" : "text-navy/60"}`}>For</div>
              <div className={`text-sm mt-1 ${lv.tone.includes("navy") ? "text-white/85" : "text-navy/80"}`}>{lv.for}</div>
              <div className={`text-xs uppercase tracking-wider mt-5 ${lv.tone.includes("navy") ? "text-gold" : "text-navy/60"}`}>Demonstrates</div>
              <ul className="mt-2 space-y-1.5">
                {lv.demonstrates.map((d) => (
                  <li key={d} className={`flex items-baseline gap-2 text-sm ${lv.tone.includes("navy") ? "text-white/90" : "text-navy/80"}`}>
                    <span className={lv.tone.includes("navy") ? "text-gold" : "text-gold"}>◆</span> {d}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Requirements */}
      <section data-testid="rcp-requirements">
        <div className="label-eyebrow text-gold">How you earn it</div>
        <h2 className="serif-display text-3xl md:text-4xl text-navy mt-2">Certification requirements.</h2>
        <p className="mt-4 max-w-3xl text-navy/70">To become an RCP, every candidate completes a comprehensive evaluation:</p>
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {REQUIREMENTS.map((r, i) => (
            <div key={r} className="border border-navy/10 bg-white p-5 flex items-start gap-4" data-testid={`rcp-req-${i+1}`}>
              <div className="serif-display text-2xl text-gold leading-none">{String(i+1).padStart(2, "0")}</div>
              <div className="text-navy/85 text-sm leading-snug pt-1">{r}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits — dual column */}
      <section className="grid lg:grid-cols-2 gap-10">
        {/* For you */}
        <div className="bg-navy text-white p-10 relative overflow-hidden" data-testid="rcp-benefits-professional">
          <div className="label-eyebrow text-gold">For you — the professional</div>
          <h2 className="serif-display text-3xl md:text-4xl mt-3 leading-tight">Beyond a certificate.</h2>
          <p className="mt-4 text-white/75 leading-relaxed">RCPs join a curated community and gain access to career infrastructure most professionals build alone.</p>
          <div className="mt-8 space-y-5">
            {PRO_BENEFITS.map(([t, d]) => (
              <div key={t} className="border-t border-gold/30 pt-4">
                <div className="serif-display text-lg text-gold">{t}</div>
                <div className="text-sm text-white/75 mt-1 leading-snug">{d}</div>
              </div>
            ))}
          </div>
        </div>

        {/* For employers */}
        <div className="border border-navy/10 bg-white p-10" data-testid="rcp-benefits-employer">
          <div className="label-eyebrow text-gold">For employers</div>
          <h2 className="serif-display text-3xl md:text-4xl mt-3 leading-tight text-navy">Why hiring managers trust RCPs.</h2>
          <p className="mt-4 text-navy/70 leading-relaxed">Organizations hiring Ravemora Certified Professionals get candidates who are already:</p>
          <ul className="mt-6 space-y-3">
            {EMP_BENEFITS.map((b, i) => (
              <li key={b} className="flex items-baseline gap-3 border-b border-navy/10 pb-3">
                <span className="serif-display text-gold text-lg">{String(i+1).padStart(2, "0")}</span>
                <span className="text-navy/85">{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 p-4 bg-gold/10 border-l-4 border-gold text-navy text-sm leading-relaxed">
            <BadgeCheck size={16} className="inline-block mr-2 text-gold" />
            The net effect for employers: <strong>reduced onboarding time, lower hiring risk, and faster time-to-productivity.</strong>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-navy/10 pt-10 grid md:grid-cols-[2fr_1fr] gap-8 items-end" data-testid="rcp-cta">
        <div>
          <div className="label-eyebrow text-gold">Ready to be an RCP?</div>
          <h2 className="serif-display text-3xl md:text-5xl leading-tight mt-4 text-navy max-w-3xl">
            Enrol interest and we'll <span className="text-gold">reach out with the next intake.</span>
          </h2>
        </div>
        <div className="text-left md:text-right">
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="rcp-cta-contact">
            Enrol interest <ArrowUpRight size={16}/>
          </Link>
        </div>
      </section>
    </div>
  );
}
